import { PreviewData } from 'next';
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { formatPrismicData } from '../utils/prismic';
import { IPrismicLink, ISearchProduct } from '@monofront/types';
import { logger } from '@monofront/logging';
import { PrismicService } from '../services';

interface IEditorialSidePanelPrismicContent<TSlice = unknown> {
  title?: string | null;
  footerCtaLabel?: string | null;
  footerCtaUrl?: IPrismicLink;
  slices?: Array<TSlice | string>;
}

interface IEditorialSidePanelContent<TSlice = unknown>
  extends IEditorialSidePanelPrismicContent<TSlice> {
  errorMessage?: string | null;
}

type SidePanelProps = Partial<ISearchProduct> & Pick<ISearchProduct, 'title'>;

export interface IEditorialSidePanelContextProps<TSlice = unknown> {
  editorialPanelSourceUid: string | null;
  setEditorialPanelSourceUid: Dispatch<SetStateAction<string | null>>;
  prismicContent: IEditorialSidePanelContent<TSlice> | null;
  isLoading: boolean;
  editorialSidePanelProps: Record<string, Partial<SidePanelProps>>;
  setEditorialSidePanelProps: Dispatch<
    SetStateAction<Record<string, Partial<SidePanelProps>>>
  >;
}
type TSlice = any;

// Create the context with a generic type
const EditorialSidePanelContext =
  createContext<IEditorialSidePanelContextProps>(
    {} as IEditorialSidePanelContextProps,
  );

export type EditorialSidePanelContextProviderProps = {
  prismicService: PrismicService;
  children: React.ReactNode;
  previewData: PreviewData;
  errorContent?: IEditorialSidePanelContent<TSlice>;
  locale: string;
};

export const EditorialSidePanelContextProvider: React.FC<
  EditorialSidePanelContextProviderProps
> = ({ children, previewData, errorContent, prismicService, locale }) => {
  const [editorialPanelSourceUid, setEditorialPanelSourceUid] = useState<
    string | null
  >(null);
  const [editorialSidePanelProps, setEditorialSidePanelProps] = useState({});
  const [prismicContent, setPrismicContent] =
    useState<IEditorialSidePanelContent<TSlice> | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const getPanel = async () => {
      if (!editorialPanelSourceUid) return;

      setIsLoading(true);

      try {
        const document = await prismicService.getDocumentByUid(
          'side_panel',
          editorialPanelSourceUid,
          previewData,
          locale ? { lang: `${locale}-${locale}` } : {},
        );

        // FIXME: fixed in !1994
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setPrismicContent(formatPrismicData(document.data));
      } catch (error) {
        logger.error('Cannot load side panel content.', {
          error,
          uid: editorialPanelSourceUid,
        });
        setPrismicContent({
          title: errorContent?.title,
          errorMessage: errorContent?.errorMessage,
        });
      }

      setIsLoading(false);
    };

    getPanel();
  }, [editorialPanelSourceUid]);

  return (
    <EditorialSidePanelContext.Provider
      value={useMemo(
        () => ({
          editorialPanelSourceUid,
          setEditorialPanelSourceUid,
          prismicContent,
          isLoading,
          editorialSidePanelProps,
          setEditorialSidePanelProps,
        }),
        [
          editorialPanelSourceUid,
          setEditorialPanelSourceUid,
          prismicContent,
          isLoading,
          editorialSidePanelProps,
          setEditorialSidePanelProps,
        ],
      )}
    >
      {children}
    </EditorialSidePanelContext.Provider>
  );
};

export const useEditorialSidePanelContext = () =>
  useContext(EditorialSidePanelContext);
